import { Fragment, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ArrowLeft, Phone, ClipboardList } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { PublicCateringMenuMatrix } from '@/components/catering/PublicCateringMenuMatrix'
import {
  useUnlockCateringQuote, useSubmitCateringQuote, useCateringQuoteBranding, selectionKey, groupByCategory, eventSectionBuckets,
  type UnlockResult, type SubmitItem, type SelectionEntry, type TrayKey, type PublicOrderHeader,
} from '@/hooks/usePublicCateringQuote'
import type { TraySize, FulfillmentType, CateringSectionKey } from '@/types/app'

type Step = 'passcode' | 'build' | 'browse' | 'submitted'

const TRAY_LABELS: Record<TrayKey, string> = {
  half_tray:    'Small tray',
  medium_tray:  'Medium tray',
  full_tray:    'Large tray',
  per_piece:    'Per piece',
  per_lb:       'Per lb (loose)',
  boxed_per_lb: 'Per lb (boxed)',
}

const ERROR_MESSAGES: Record<string, string> = {
  not_found: "We couldn't find that order with this phone number. Double-check it matches what you gave the restaurant.",
  invalid_request: 'Please enter the phone number on file for this order.',
  no_items: 'Pick at least one item before submitting.',
  already_quoted: 'This order has moved past the quoting stage and can no longer be edited here — please contact the restaurant directly.',
  below_minimum: "One of your items is under its minimum order quantity — check the per-lb items in your order and increase the quantity.",
}

function friendlyError(message: string) {
  return ERROR_MESSAGES[message] ?? 'Something went wrong. Please try again or contact the restaurant.'
}

// Simple three-stage view for customers — never the full internal lifecycle
// (deposit_paid, in_production, etc. all just read as "Confirmed" here).
const PUBLIC_STATUS: Record<string, { label: string; variant: 'neutral' | 'warning' | 'success' | 'danger' }> = {
  inquiry:       { label: 'Inquiry',   variant: 'neutral' },
  quoted:        { label: 'Quoted',    variant: 'warning' },
  deposit_paid:  { label: 'Confirmed', variant: 'success' },
  confirmed:     { label: 'Confirmed', variant: 'success' },
  in_production: { label: 'Confirmed', variant: 'success' },
  delivered:     { label: 'Confirmed', variant: 'success' },
  completed:     { label: 'Confirmed', variant: 'success' },
  cancelled:     { label: 'Cancelled', variant: 'danger' },
}

// Top info card — branding, order #, a plain (non-progress) status pill, and
// the customer/event details strip. Shown on every unlocked view (editable or
// not) so there's one consistent place to see "what did I tell them" without
// a separate confirm-your-details step.
function OrderInfoCard({
  unlocked, fulfillmentChoice, onFulfillmentChange,
}: {
  unlocked: UnlockResult
  // Only present while the order is still editable — the one place this is
  // ever settable, so it doesn't need to repeat anywhere else on the page.
  fulfillmentChoice?: FulfillmentType | null
  onFulfillmentChange?: (choice: FulfillmentType) => void
}) {
  const restaurant = unlocked.restaurant
  const contact = unlocked.contact
  const order = unlocked.order
  const status = PUBLIC_STATUS[order.status] ?? { label: order.status, variant: 'neutral' as const }

  return (
    <Card className="overflow-hidden" padding="none">
      <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-3">
        {restaurant?.logo_url ? (
          <img src={restaurant.logo_url} alt={restaurant.name} className="h-10 w-10 rounded-lg object-cover shrink-0" />
        ) : restaurant?.name ? (
          <div
            className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold shrink-0"
            style={{ backgroundColor: restaurant.brand_colour_primary ?? '#c94a1a' }}
          >
            {restaurant.name.charAt(0).toUpperCase()}
          </div>
        ) : null}
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-stone-900 truncate">{restaurant?.name ?? 'Catering Order'}</p>
          <p className="text-xs text-stone-500">Order {order.order_number}</p>
        </div>
        <Badge variant={status.variant}>{status.label}</Badge>
      </div>

      <div className="px-4 py-3 grid grid-cols-2 sm:grid-cols-3 gap-3">
        <InfoField label="Name" value={order.client_name} />
        <InfoField label="Phone Number" value={order.client_phone} />
        <InfoField label="Event Date" value={order.event_date} />
        <InfoField label="Event Time" value={order.event_time ?? '—'} />
        <InfoField label="Guest Count" value={order.guest_count != null ? String(order.guest_count) : '—'} />
        <div className="flex flex-col gap-0.5">
          <span className="text-2xs font-medium uppercase tracking-wide text-stone-400">Delivery or Pickup</span>
          {onFulfillmentChange ? (
            <div className="flex gap-1 -ml-0.5">
              {(['pickup', 'delivery'] as const).map(opt => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => onFulfillmentChange(opt)}
                  className={[
                    'px-2 py-0.5 rounded text-xs font-medium border transition-colors',
                    fulfillmentChoice === opt
                      ? 'bg-brand-600 border-brand-600 text-white'
                      : 'bg-white border-stone-200 text-stone-600 hover:border-stone-300',
                  ].join(' ')}
                >
                  {opt === 'pickup' ? 'Pickup' : 'Delivery'}
                </button>
              ))}
            </div>
          ) : (
            <span className="text-sm font-medium text-stone-900">
              {order.fulfillment_type === 'delivery' ? 'Delivery' : order.fulfillment_type === 'pickup' ? 'Pickup' : 'To be confirmed'}
            </span>
          )}
        </div>
      </div>

      {contact && (
        <div className="px-4 py-2.5 border-t border-stone-100 bg-stone-50">
          <p className="inline-flex items-center gap-1.5 text-xs text-stone-500">
            <Phone size={11} />
            Your contact: <strong className="text-stone-700">{contact.name}</strong>
            {contact.phone && <> · {contact.phone}</>}
          </p>
        </div>
      )}
    </Card>
  )
}

function InfoField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 min-w-0">
      <span className="text-2xs font-medium uppercase tracking-wide text-stone-400">{label}</span>
      <span className="text-sm font-medium text-stone-900 truncate">{value}</span>
    </div>
  )
}

export function CateringQuotePage() {
  const { orderNumber } = useParams<{ orderNumber: string }>()
  const [passcode, setPasscode] = useState('')
  const [unlocked, setUnlocked] = useState<UnlockResult | null>(null)
  const [step, setStep] = useState<Step>('passcode')
  const [selections, setSelections] = useState<Map<string, SelectionEntry>>(new Map())
  const [notes, setNotes] = useState('')
  // Customer's own pick — staff still confirms the address is deliverable and
  // sets/prices the actual fee on their side, but the customer telling us
  // which they want is the missing first step that kicks that off.
  const [fulfillmentChoice, setFulfillmentChoice] = useState<FulfillmentType | null>(null)

  const unlock = useUnlockCateringQuote()
  const submit = useSubmitCateringQuote()
  // Passcode-free branding lookup so the phone-number screen itself is
  // branded, before the customer has proven who they are.
  const { data: preAuthBranding } = useCateringQuoteBranding(orderNumber)

  const showPricing = unlocked?.showPricing ?? false

  function handleUnlock(e: React.FormEvent) {
    e.preventDefault()
    if (!orderNumber) return
    unlock.mutate({ orderNumber, passcode }, {
      onSuccess: (result) => {
        setUnlocked(result)
        if (result.editable) {
          const initial = new Map<string, SelectionEntry>()
          for (const sel of result.selected) {
            if (sel.menu_item_id && sel.tray_size && sel.tray_size !== 'custom') {
              const key = selectionKey(sel.menu_item_id, sel.tray_size)
              initial.set(key, {
                itemId: sel.menu_item_id, traySize: sel.tray_size, quantity: sel.quantity, notes: sel.notes,
                isComplimentary: !!sel.is_complimentary,
                sectionId: sel.catering_section ?? null,
              })
            }
          }
          setSelections(initial)
          setNotes(result.order.special_requests ?? '')
          setFulfillmentChoice(result.order.fulfillment_type ?? null)
        }
        setStep('build')
      },
    })
  }

  function handleSubmit() {
    if (!orderNumber) return
    const items: SubmitItem[] = [...selections.values()]
      .filter(s => s.quantity > 0)
      .map(s => ({
        menu_item_id: s.itemId, tray_size: s.traySize, quantity: s.quantity, notes: s.notes,
        catering_section: s.sectionId,
      }))
    if (items.length === 0) return
    // Fulfillment type is the customer's request — staff still confirms the
    // address is deliverable and sets the actual delivery fee afterward, this
    // just tells them which the customer wants.
    submit.mutate(
      { orderNumber, passcode, items, notes: notes.trim() || undefined, fulfillmentType: fulfillmentChoice },
      { onSuccess: () => setStep('submitted') },
    )
  }

  const menuGroups = useMemo(() => {
    if (!unlocked?.editable) return []
    const mains  = unlocked.items.filter(i => !i.is_addon)
    const addons = unlocked.items.filter(i => i.is_addon)
    const byCategory = new Map<string, typeof mains>()
    for (const item of mains) {
      const key = item.category_id ?? 'uncategorized'
      byCategory.set(key, [...(byCategory.get(key) ?? []), item])
    }
    const catName = (id: string) => unlocked.categories.find(c => c.id === id)?.name ?? 'Other'
    const groups = [...byCategory.entries()]
      .sort(([a], [b]) => (unlocked.categories.findIndex(c => c.id === a)) - (unlocked.categories.findIndex(c => c.id === b)))
      .map(([id, items]) => ({ id, name: catName(id), items }))
    if (addons.length > 0) groups.push({ id: 'addons', name: 'Add-ons', items: addons })
    return groups
  }, [unlocked])

  // Flat lookup of every browsable item, so the "your order" summary can show
  // name/size/price for whatever's currently selected.
  const itemById = useMemo(() => {
    const map = new Map<string, (typeof menuGroups)[number]['items'][number]>()
    for (const g of menuGroups) for (const i of g.items) map.set(i.id, i)
    return map
  }, [menuGroups])

  const selectedEntries = [...selections.values()].filter(s => s.quantity > 0)
  const selectedCount = selectedEntries.length
  const guestCount = unlocked?.editable ? unlocked.order.guest_count : null

  // Complimentary items contribute $0 — staff already excluded them from the
  // total on their side, the customer's estimate has to match that exactly.
  const estimatedTotal = selectedEntries.reduce((sum, s) => {
    if (s.isComplimentary) return sum
    const item = itemById.get(s.itemId)
    const price = item?.tray_options[s.traySize]?.price
    return sum + (price ?? 0) * s.quantity
  }, 0)

  // What all the complimentary items would have cost at full price — shown
  // to the customer as its own savings line so the value is visible, not
  // just implied by the item rows saying "Comp".
  const complimentaryValue = selectedEntries.reduce((sum, s) => {
    if (!s.isComplimentary) return sum
    const item = itemById.get(s.itemId)
    const price = item?.tray_options[s.traySize]?.price
    return sum + (price ?? 0) * s.quantity
  }, 0)

  function toggleSelection(itemId: string, traySize: TrayKey, min?: number | null) {
    const key = selectionKey(itemId, traySize)
    setSelections(prev => {
      const next = new Map(prev)
      // Tapping a size in Browse always starts a fresh, non-complimentary,
      // unsectioned line — comp status is staff-only, never something
      // re-adding an item via the matrix should inherit or grant. Section can
      // be picked afterward in "Your order" (see setSection below). Starting
      // quantity is that size's minimum order (per-lb sizes) if it has one.
      if (next.has(key)) next.delete(key)
      else next.set(key, { itemId, traySize, quantity: min ?? 1, notes: null, isComplimentary: false, sectionId: null })
      return next
    })
  }

  function setQty(itemId: string, traySize: TrayKey, raw: string) {
    const key = selectionKey(itemId, traySize)
    // Weight-based sizes take decimal lbs (e.g. "10.5") — every other size
    // is a whole-unit count, same as before.
    const isWeightBased = traySize === 'per_lb' || traySize === 'boxed_per_lb'
    const n = isWeightBased
      ? parseFloat(raw.replace(/[^\d.]/g, ''))
      : parseInt(raw.replace(/\D/g, ''), 10)
    setSelections(prev => {
      const current = prev.get(key)
      if (!current) return prev
      const next = new Map(prev)
      next.set(key, { ...current, quantity: isNaN(n) || n < (isWeightBased ? 0.01 : 1) ? (isWeightBased ? 0.01 : 1) : n })
      return next
    })
  }

  function setSection(itemId: string, traySize: TrayKey, sectionId: CateringSectionKey) {
    const key = selectionKey(itemId, traySize)
    setSelections(prev => {
      const current = prev.get(key)
      if (!current) return prev
      const next = new Map(prev)
      next.set(key, { ...current, sectionId })
      return next
    })
  }

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-8">
      <div className="max-w-2xl mx-auto space-y-4">
        {!unlocked && (
          <div className="text-center space-y-2">
            {preAuthBranding?.restaurant?.logo_url ? (
              <img
                src={preAuthBranding.restaurant.logo_url}
                alt={preAuthBranding.restaurant.name}
                className="h-14 w-14 mx-auto rounded-xl object-cover shadow-sm"
              />
            ) : preAuthBranding?.restaurant?.name ? (
              <div
                className="h-14 w-14 mx-auto rounded-xl flex items-center justify-center text-white font-bold text-xl shadow-sm"
                style={{ backgroundColor: preAuthBranding.restaurant.brand_colour_primary ?? '#c94a1a' }}
              >
                {preAuthBranding.restaurant.name.charAt(0).toUpperCase()}
              </div>
            ) : null}
            <h1 className="text-lg font-semibold text-stone-900">
              {preAuthBranding?.restaurant?.name ?? 'Catering Menu Selection'}
            </h1>
            <p className="text-sm text-stone-500">Order {orderNumber}</p>
          </div>
        )}

        {step === 'passcode' && (
          <Card>
            <CardContent>
              <form onSubmit={handleUnlock} className="space-y-4">
                <Input
                  label="Your phone number"
                  type="tel"
                  inputMode="tel"
                  value={passcode}
                  onChange={e => setPasscode(e.target.value)}
                  placeholder="(555) 123-4567"
                  hint="Enter the full phone number you gave the restaurant."
                  required
                />
                {unlock.isError && <p className="text-sm text-danger">{friendlyError(unlock.error.message)}</p>}
                <Button type="submit" className="w-full" loading={unlock.isPending} disabled={passcode.replace(/\D/g, '').length < 7}>
                  Continue
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {unlocked && step !== 'passcode' && (
          <OrderInfoCard
            unlocked={unlocked}
            fulfillmentChoice={unlocked.editable && step !== 'submitted' ? fulfillmentChoice : undefined}
            onFulfillmentChange={unlocked.editable && step !== 'submitted' ? setFulfillmentChoice : undefined}
          />
        )}

        {unlocked && !unlocked.editable && step !== 'submitted' && (
          <Card>
            <CardContent className="space-y-3">
              <p className="text-sm text-stone-600">
                We've already started preparing your order, so this menu can't be changed here anymore.
                Please contact us directly if you'd like to make changes.
              </p>
              {unlocked.items.length > 0 && (
                <OrderItemsTable
                  sections={groupByCategory(
                    unlocked.items.map((it, idx) => ({
                      categoryId: it.catering_section ?? null,
                      key: `${it.menu_item_id ?? it.name}:${idx}`,
                      name: it.name,
                      traySize: it.tray_size,
                      quantity: it.quantity,
                      price: it.unit_price,
                      notes: it.notes,
                      isComplimentary: !!it.is_complimentary,
                    })),
                    eventSectionBuckets(unlocked.order.event_sections),
                  )}
                  showPricing={showPricing}
                />
              )}
              {showPricing && unlocked.order.quote_amount != null && (
                <TotalsBreakdown
                  order={unlocked.order}
                  complimentaryValue={unlocked.items.reduce(
                    (s, it) => s + (it.is_complimentary ? (it.unit_price ?? 0) * it.quantity : 0), 0,
                  )}
                />
              )}
            </CardContent>
          </Card>
        )}

        {/* ── "Your order" summary screen — review, adjust qty, notes, submit ── */}
        {step === 'build' && unlocked?.editable && (
          <>
            <Button variant="outline" className="w-full" onClick={() => setStep('browse')}>
              <ClipboardList size={15} />
              {selectedCount === 0 ? 'Browse the menu to get started' : 'Browse & add more items'}
            </Button>

            <Card>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <h2 className="font-semibold text-stone-900">Your order</h2>
                  {guestCount && <p className="text-xs text-stone-400">Planning for ~{guestCount} guests</p>}
                </div>

                {selectedCount === 0 ? (
                  <p className="text-sm text-stone-400 text-center py-4">No items yet — browse the menu above to add some.</p>
                ) : (
                  <OrderItemsTable
                    sections={groupByCategory(
                      selectedEntries.map(s => ({
                        categoryId: s.sectionId,
                        key: selectionKey(s.itemId, s.traySize),
                        name: itemById.get(s.itemId)?.name ?? '—',
                        traySize: s.traySize,
                        quantity: s.quantity,
                        price: itemById.get(s.itemId)?.tray_options[s.traySize]?.price ?? null,
                        minQty: itemById.get(s.itemId)?.tray_options[s.traySize]?.min ?? null,
                        notes: s.notes,
                        isComplimentary: s.isComplimentary,
                        onQtyChange: (raw: string) => setQty(s.itemId, s.traySize, raw),
                        sectionId: s.sectionId,
                        onSectionChange: (id: CateringSectionKey) => setSection(s.itemId, s.traySize, id),
                      })),
                      eventSectionBuckets(unlocked.order.event_sections),
                    )}
                    showPricing={showPricing}
                    allSections={eventSectionBuckets(unlocked.order.event_sections)}
                  />
                )}

                {showPricing && selectedCount > 0 ? (
                  <div className="flex flex-col items-end text-sm pt-2 border-t border-stone-100">
                    <div className="flex justify-between w-full text-stone-500 text-xs">
                      <span>Items</span><span>${estimatedTotal.toFixed(2)}</span>
                    </div>
                    {complimentaryValue > 0 && (
                      <div className="flex justify-between w-full text-success-dark text-xs">
                        <span>Complimentary items</span><span>−${complimentaryValue.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between w-full text-stone-500 text-xs">
                      <span>+ 7% tax</span><span>${(estimatedTotal * TAX_RATE).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between w-full font-semibold text-stone-900 pt-1">
                      <span>Estimated total</span><span>${(estimatedTotal * (1 + TAX_RATE)).toFixed(2)}</span>
                    </div>
                    <span className="text-2xs text-stone-400 mt-1">
                      Delivery fee and any discount will be added and confirmed once your order is finalized.
                    </span>
                  </div>
                ) : selectedCount > 0 ? (
                  <p className="text-2xs text-stone-400 text-right">
                    We'll go over pricing together before anything's finalized.
                  </p>
                ) : null}

                <div>
                  <label className="text-sm font-medium text-stone-700">Anything else we should know?</label>
                  <textarea
                    rows={3}
                    value={notes}
                    onChange={e => setNotes(e.target.value)}
                    placeholder="Allergies, delivery instructions, questions, etc."
                    className="mt-1 w-full border border-stone-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-400 resize-none"
                  />
                </div>
              </CardContent>
            </Card>

            <div className="sticky bottom-4 pt-2 space-y-2">
              <Card className="shadow-lg">
                <CardContent className="flex items-center justify-between">
                  <span className="text-sm text-stone-600">{selectedCount} item{selectedCount === 1 ? '' : 's'} selected</span>
                  <Button onClick={handleSubmit} loading={submit.isPending} disabled={selectedCount === 0}>
                    Submit Selections
                  </Button>
                </CardContent>
              </Card>
              {submit.isError && <p className="text-sm text-danger text-center">{friendlyError(submit.error.message)}</p>}

              {unlocked.order.status === 'inquiry' && (
                <Card padding="sm" className="bg-stone-100/60 border-stone-200">
                  <p className="text-2xs font-semibold uppercase tracking-wide text-stone-400 mb-1">Before you submit</p>
                  <p className="text-xs text-stone-500">
                    This sends us your list — not a final order. We'll review it and reach out to talk through details,
                    dietary needs, and pricing. Questions before then? You can reach out to us too.
                  </p>
                </Card>
              )}
            </div>
          </>
        )}

        {/* ── Browse screen — full menu matrix, tap to add, then come back ── */}
        {step === 'browse' && unlocked?.editable && (
          <>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setStep('build')}
                className="p-1.5 -ml-1.5 rounded hover:bg-stone-100 text-stone-500"
                title="Back to your order"
              >
                <ArrowLeft size={18} />
              </button>
              <div>
                <h2 className="text-sm font-semibold text-stone-700">Browse the full menu</h2>
                <p className="text-xs text-stone-400">Tap a size to add · tap again to remove · then come back to your order</p>
              </div>
            </div>

            <PublicCateringMenuMatrix
              groups={menuGroups}
              selections={selections}
              onToggle={toggleSelection}
              showCost={showPricing}
            />

            <div className="sticky bottom-4 pt-2">
              <Button className="w-full shadow-lg" onClick={() => setStep('build')}>
                Done browsing — back to your order ({selectedCount})
              </Button>
            </div>
          </>
        )}

        {step === 'submitted' && (
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-stone-900 font-medium">Thanks! We've received your selections.</p>
              <p className="text-sm text-stone-500 mt-1">
                We'll review them and get in touch to confirm details, dietary needs, and pricing — or feel free to
                reach out to us in the meantime.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}

const TAX_RATE = 0.07

// Delivery fee / discount are only ever shown here — once the order is
// confirmed and no longer editable on this page — never as a live estimate
// during quoting, so nothing shown to the customer changes after the fact.
function TotalsBreakdown({ order, complimentaryValue = 0 }: { order: PublicOrderHeader; complimentaryValue?: number }) {
  // order.quote_amount is already tax-INCLUSIVE (what's actually owed) —
  // back out the pre-tax figure for the "Tax (7%)" line rather than adding
  // tax on top again, which would double it.
  const withTax    = order.quote_amount ?? 0
  const preTax     = withTax / (1 + TAX_RATE)
  const taxAmt     = withTax - preTax
  const deliveryCharge = order.delivery_is_complimentary ? 0 : (order.delivery_fee ?? 0)
  // Reconstruct the items subtotal the same way it was built staff-side:
  // preTax = itemsSubtotal - discount + deliveryCharge.
  const itemsSubtotal = preTax + (order.discount_amount ?? 0) - deliveryCharge
  return (
    <div className="pt-2 border-t border-stone-100 space-y-1 text-sm">
      {/* Same line order everywhere: staff's Menu & Payment card, Quote
          Preview, print, Excel export, and here — Items subtotal → Comp →
          Discount → Delivery → Subtotal → tax → Total with tax. */}
      <div className="flex justify-between text-stone-500">
        <span>Items subtotal</span><span>${itemsSubtotal.toFixed(2)}</span>
      </div>
      {complimentaryValue > 0 && (
        <div className="flex justify-between text-success-dark">
          <span>Complimentary items</span><span>−${complimentaryValue.toFixed(2)}</span>
        </div>
      )}
      {!!order.discount_amount && (
        <div className="flex justify-between text-stone-500">
          <span>Discount{order.discount_percentage ? ` (${order.discount_percentage.toFixed(0)}%)` : ''}</span>
          <span>−${order.discount_amount.toFixed(2)}</span>
        </div>
      )}
      {!!order.delivery_fee && (
        <div className="flex justify-between text-stone-500">
          <span>Delivery fee</span>
          <span>{order.delivery_is_complimentary ? '' : '+'}${order.delivery_fee.toFixed(2)}</span>
        </div>
      )}
      {!!order.delivery_fee && order.delivery_is_complimentary && (
        <div className="flex justify-between text-success-dark">
          <span>Delivery waived (comp)</span><span>−${order.delivery_fee.toFixed(2)}</span>
        </div>
      )}
      <div className="flex justify-between font-semibold text-stone-900 pt-1 border-t border-stone-100">
        <span>Subtotal</span><span>${preTax.toFixed(2)}</span>
      </div>
      <div className="flex justify-between text-stone-500 text-xs">
        <span>+ 7% tax</span><span>${taxAmt.toFixed(2)}</span>
      </div>
      <div className="flex justify-between font-bold text-stone-900 pt-1 border-t border-stone-100">
        <span>Total with tax</span><span>${withTax.toFixed(2)}</span>
      </div>
    </div>
  )
}

// Shared item table for both the read-only (post-quoted) view and the
// editable "your order" build screen — Item / Qty / Serves / Notes, matching
// the internal order card layout customers already see referenced from staff.
interface OrderItemRow {
  key: string
  name: string
  traySize: TraySize | null
  quantity: number
  price: number | null
  // Per-lb sizes only — minimum order (lbs), for the below-minimum hint.
  minQty?: number | null
  notes: string | null
  isComplimentary: boolean
  onQtyChange?: (raw: string) => void
  // Only set on unsectioned rows in the editable "your order" screen — lets
  // the customer tag which meal an item belongs to themselves instead of
  // waiting for staff to do it after the fact.
  sectionId?: CateringSectionKey | null
  onSectionChange?: (sectionId: CateringSectionKey) => void
}

// Renders items grouped into category sections (Appetizers, Currys, Rice, …)
// in the same order as the rest of the menu, with a header row per category —
// same visual convention used on the Products page and the browse matrix, so
// customers see the same organization staff does. Removal happens by
// re-tapping the item's size in Browse, not from this table.
function OrderItemsTable({
  sections, showPricing, allSections,
}: {
  sections: { id: string; name: string; rows: OrderItemRow[] }[]
  showPricing: boolean
  // Full section list — only needed to populate the "move to section" picker
  // on unsectioned rows in the editable build screen.
  allSections?: { id: string; name: string }[]
}) {
  return (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-stone-50 border-b border-stone-200">
          <tr>
            <th className="px-3 py-2 text-left font-medium text-stone-500 text-xs">Item</th>
            <th className="px-3 py-2 text-center font-medium text-stone-500 text-xs">Qty</th>
            {showPricing && <th className="px-3 py-2 text-right font-medium text-stone-500 text-xs">Price</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-stone-100">
          {sections.map(section => (
            <Fragment key={section.id}>
              <tr className="bg-stone-50">
                <td colSpan={showPricing ? 3 : 2} className="px-3 py-1 text-2xs font-bold uppercase tracking-widest text-stone-400 border-y border-stone-200">
                  {section.name}
                </td>
              </tr>
              {section.rows.map(it => (
                <tr key={it.key}>
                  <td className="px-3 py-2">
                    <p className="font-medium text-stone-800">{it.name}</p>
                    <p className="text-2xs text-stone-400">
                      {it.traySize && it.traySize !== 'custom' ? TRAY_LABELS[it.traySize] : null}
                    </p>
                    {it.notes && <p className="text-2xs text-stone-400 italic mt-0.5">{it.notes}</p>}
                    {it.onSectionChange && !it.sectionId && allSections && allSections.length > 0 && (
                      <div className="flex flex-wrap items-center gap-1 mt-0.5">
                        {allSections.map(s => (
                          <button
                            key={s.id}
                            type="button"
                            onClick={() => it.onSectionChange!(s.id as CateringSectionKey)}
                            className="px-1.5 py-0.5 rounded-full border border-brand-200 bg-brand-50 text-2xs font-medium text-brand-700 hover:bg-brand-100 hover:border-brand-300"
                          >
                            → {s.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {it.onQtyChange ? (
                      <>
                        <input
                          type="text"
                          inputMode={it.traySize === 'per_lb' || it.traySize === 'boxed_per_lb' ? 'decimal' : 'numeric'}
                          value={it.quantity}
                          onFocus={e => e.target.select()}
                          onChange={e => it.onQtyChange!(e.target.value)}
                          className="w-12 text-center border border-stone-300 rounded px-1.5 py-1 focus:outline-none focus:ring-1 focus:ring-brand-400"
                        />
                        {it.minQty != null && it.quantity < it.minQty && (
                          <p className="text-2xs text-warning-dark mt-0.5">min {it.minQty} lb</p>
                        )}
                      </>
                    ) : (
                      <span className="text-stone-700">×{it.quantity}</span>
                    )}
                  </td>
                  {showPricing && (
                    <td className="px-3 py-2 text-right">
                      {it.isComplimentary ? (
                        <span className="text-success-dark font-medium text-xs">Free</span>
                      ) : it.price != null ? (
                        <span className="text-stone-700">${(it.price * it.quantity).toFixed(2)}</span>
                      ) : (
                        <span className="text-stone-300">—</span>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}
